import React, {useState, useCallback, useContext, useEffect, useRef} from 'react';
import {multiFactor, TotpMultiFactorGenerator, PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier, EmailAuthProvider, reauthenticateWithCredential} from 'firebase/auth';
import {doc, updateDoc} from 'firebase/firestore';
import {QRCodeSVG} from 'qrcode.react';
import {useConfirm} from 'material-ui-confirm';
import {useSnackbar} from 'notistack';
import {addBreadcrumb, logger} from '@sentry/react';

import {auth, db} from '-/firebase';
import {reportError} from '-/data/errorReporting';
import {SettingsContext} from '-/contexts/Settings';

const mfaBreadcrumb = (message, data) => addBreadcrumb({
    category: 'mfa',
    message,
    level: 'info',
    data: {uid: auth.currentUser?.uid, ...data}
});

const mfaEvent = (message, data) => logger.info(message, {
    feature: 'mfa',
    uid: auth.currentUser?.uid,
    ...data
});

import ResponsiveDialog from '-/components/ResponsiveDialog';
import {MuiTelInput} from 'mui-tel-input';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import PhoneIcon from '@mui/icons-material/Phone';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const FACTOR_LABELS = {
    totp: 'Authenticator App',
    phone: 'SMS'
};

function getEnrolledFactor(user) {
    if (!user) return null;
    const factors = multiFactor(user).enrolledFactors;
    if (factors.length === 0) return null;
    return factors[0];
}

export default function MFAEnrollment({phone, dialogOnly, open: externalOpen, onClose: externalOnClose, onEnrolled}) {
    const user = auth.currentUser;
    const {siteName = 'Ember Tracking'} = useContext(SettingsContext);
    const [enrolledFactor, setEnrolledFactor] = useState(() => getEnrolledFactor(user));
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = dialogOnly;
    const open = isControlled ? externalOpen : internalOpen;
    const [step, setStep] = useState('choose'); // 'choose' | 'totp' | 'sms' | 'sms-verify' | 'reauth'
    const [totpSecret, setTotpSecret] = useState(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationId, setVerificationId] = useState(null);
    const [smsCode, setSmsCode] = useState('');
    const [smsSent, setSmsSent] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reauthPassword, setReauthPassword] = useState('');
    const [pendingAction, setPendingAction] = useState(null); // 'totp' | 'sms'
    const recaptchaRef = useRef(null);
    const recaptchaVerifierRef = useRef(null);
    const confirm = useConfirm();
    const {enqueueSnackbar} = useSnackbar();

    useEffect(() => {
        return () => {
            if (recaptchaVerifierRef.current) {
                recaptchaVerifierRef.current.clear();
            }
        };
    }, []);

    useEffect(() => {
        if (resendCountdown <= 0) return;
        const timer = setTimeout(() => setResendCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCountdown]);

    const handleOpen = useCallback(() => {
        setInternalOpen(true);
        setStep('choose');
        setError(null);
        setVerificationCode('');
        setSmsCode('');
        setPhoneNumber(phone || '');
        setTotpSecret(null);
        setVerificationId(null);
    }, [phone]);

    const handleClose = useCallback(() => {
        if (loading) return;
        if (isControlled) {
            externalOnClose?.();
        } else {
            setInternalOpen(false);
        }
    }, [loading, isControlled, externalOnClose]);

    useEffect(() => {
        if (open) {
            setStep('choose');
            setError(null);
            setVerificationCode('');
            setSmsCode('');
            setSmsSent(false);
            setResendCountdown(0);
            setPhoneNumber(phone || '');
            setTotpSecret(null);
            setVerificationId(null);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleReauthenticate = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const credential = EmailAuthProvider.credential(user.email, reauthPassword);
            await reauthenticateWithCredential(user, credential);
            setReauthPassword('');

            // Resume the action that triggered re-auth
            const action = pendingAction;
            setPendingAction(null);

            if (action === 'totp') {
                setStep('totp');
                const mfaUser = multiFactor(user);
                const session = await mfaUser.getSession();
                const secret = await TotpMultiFactorGenerator.generateSecret(session);
                setTotpSecret(secret);
            } else if (action === 'sms') {
                setStep('sms');
            }
        } catch (e) {
            if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
                setError('Incorrect password. Please try again.');
            } else {
                setError(e.message);
            }
        }

        setLoading(false);
    }, [user, reauthPassword, pendingAction]);

    const startTotpEnrollment = useCallback(async () => {
        setLoading(true);
        setError(null);
        setStep('totp');

        try {
            const mfaUser = multiFactor(user);
            const session = await mfaUser.getSession();
            const secret = await TotpMultiFactorGenerator.generateSecret(session);
            setTotpSecret(secret);
        } catch (e) {
            if (e.code === 'auth/requires-recent-login') {
                setPendingAction('totp');
                setStep('reauth');
                setError(null);
            } else {
                setError(e.message);
                setStep('choose');
            }
        }

        setLoading(false);
    }, [user]);

    const completeTotpEnrollment = useCallback(async pastedCode => {
        const codeToVerify = pastedCode || verificationCode;
        setLoading(true);
        setError(null);
        mfaBreadcrumb('totp: completeTotpEnrollment called');

        try {
            const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, codeToVerify);
            mfaBreadcrumb('totp: calling enroll()');
            await multiFactor(user).enroll(assertion, 'Authenticator App');
            mfaBreadcrumb('totp: enroll() resolved');

            await user.reload();
            const factor = getEnrolledFactor(user);
            mfaBreadcrumb('totp: reload returned', {hasFactor: !!factor});
            if (!factor) {
                setError('Enrollment did not persist. Please try again.');
                reportError(new Error('MFA TOTP enrollment succeeded but no factor found after reload'));
                setLoading(false);
                return;
            }
            setEnrolledFactor(factor);
            handleClose();
            onEnrolled?.();
            enqueueSnackbar('Two-factor authentication enabled', {variant: 'success'});
            mfaEvent('MFA enrolled', {method: 'totp', mfaEnrollmentId: factor.uid});
        } catch (e) {
            mfaBreadcrumb('totp: completeTotpEnrollment threw', {code: e.code});
            if (e.code === 'auth/requires-recent-login') {
                setPendingAction('totp');
                setStep('reauth');
                setError(null);
            } else if (e.code === 'auth/invalid-verification-code') {
                setError('Invalid verification code. Please try again.');
            } else if (e.code === 'auth/too-many-enrollment-attempts') {
                // Firebase invalidates the secret after too many wrong codes — the current
                // QR is now dead, so we must regenerate a fresh secret and have the user re-scan.
                // This is expected user behaviour, not a bug, so don't report it to Sentry.
                setVerificationCode('');
                setTotpSecret(null);
                try {
                    const mfaUser = multiFactor(user);
                    const session = await mfaUser.getSession();
                    const secret = await TotpMultiFactorGenerator.generateSecret(session);
                    setTotpSecret(secret);
                    setError('Too many incorrect attempts. We\'ve generated a new QR code — please scan it again and enter a fresh code.');
                } catch (regenError) {
                    if (regenError.code === 'auth/requires-recent-login') {
                        setPendingAction('totp');
                        setStep('reauth');
                        setError(null);
                    } else {
                        setError('Too many incorrect attempts. Please close and restart two-factor setup.');
                        reportError(regenError);
                    }
                }
            } else {
                setError(e.message || 'Unable to complete enrollment. Please try again.');
                reportError(e);
            }
        }

        setLoading(false);
    }, [user, totpSecret, verificationCode, enqueueSnackbar, handleClose, onEnrolled]);

    const sendSmsCode = useCallback(async () => {
        setLoading(true);
        setError(null);
        mfaBreadcrumb('sms: sendSmsCode called', {existingFactor: !!getEnrolledFactor(user)});

        try {
            // Always create a fresh RecaptchaVerifier to avoid stale state after dialog reopen
            if (recaptchaVerifierRef.current) {
                recaptchaVerifierRef.current.clear();
            }
            recaptchaVerifierRef.current = new RecaptchaVerifier(auth, recaptchaRef.current, {
                size: 'invisible'
            });

            const mfaUser = multiFactor(user);
            const multiFactorSession = await mfaUser.getSession();
            const e164Phone = '+' + phoneNumber.replace(/\D/g, '');
            const phoneInfoOptions = {
                phoneNumber: e164Phone,
                session: multiFactorSession
            };
            const phoneAuthProvider = new PhoneAuthProvider(auth);
            const id = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifierRef.current);
            mfaBreadcrumb('sms: verificationId received');
            setVerificationId(id);
            setStep('sms-verify');
            setSmsSent(true);
            setResendCountdown(60);
        } catch (e) {
            mfaBreadcrumb('sms: sendSmsCode threw', {code: e.code});
            if (e.code === 'auth/requires-recent-login') {
                setPendingAction('sms');
                setStep('reauth');
                setError(null);
            } else if (e.code === 'auth/too-many-requests') {
                setError('Too many attempts. Please wait a few minutes and try again.');
            } else {
                setError(e.message);
            }
        }

        setLoading(false);
    }, [user, phoneNumber]);

    const completeSmsEnrollment = useCallback(async pastedCode => {
        const codeToVerify = pastedCode || smsCode;
        setLoading(true);
        setError(null);
        mfaBreadcrumb('sms: completeSmsEnrollment called');

        try {
            const credential = PhoneAuthProvider.credential(verificationId, codeToVerify);
            const assertion = PhoneMultiFactorGenerator.assertion(credential);
            mfaBreadcrumb('sms: calling enroll()');
            await multiFactor(user).enroll(assertion, 'Phone Number');
            mfaBreadcrumb('sms: enroll() resolved');

            if (phoneNumber && phoneNumber !== phone) {
                const userRef = doc(db, 'users', user.uid);
                await updateDoc(userRef, {phone: phoneNumber});
            }

            await user.reload();
            const factor = getEnrolledFactor(user);
            mfaBreadcrumb('sms: reload returned', {hasFactor: !!factor});
            if (!factor) {
                setError('Enrollment did not persist. Please try again.');
                reportError(new Error('MFA SMS enrollment succeeded but no factor found after reload'));
                setLoading(false);
                return;
            }
            setEnrolledFactor(factor);
            handleClose();
            onEnrolled?.();
            enqueueSnackbar('Two-factor authentication enabled', {variant: 'success'});
            mfaEvent('MFA enrolled', {method: 'sms', mfaEnrollmentId: factor.uid});
        } catch (e) {
            mfaBreadcrumb('sms: completeSmsEnrollment threw', {code: e.code});
            if (e.code === 'auth/requires-recent-login') {
                setPendingAction('sms');
                setStep('reauth');
                setError(null);
            } else if (e.code === 'auth/invalid-verification-code') {
                setError('Invalid verification code. Please try again.');
            } else {
                setError(e.message || 'Unable to complete enrollment. Please try again.');
                reportError(e);
            }
        }

        setLoading(false);
    }, [user, verificationId, smsCode, phoneNumber, phone, enqueueSnackbar, handleClose, onEnrolled]);

    const handleUnenroll = useCallback(async () => {
        const {confirmed} = await confirm({
            title: 'Disable Two-Factor Authentication',
            description: 'Are you sure you want to disable two-factor authentication? Your account will be less secure.',
            confirmationText: 'Disable'
        });

        if (!confirmed) return;

        setLoading(true);
        setError(null);

        try {
            const factor = getEnrolledFactor(user);
            mfaEvent('MFA unenroll requested', {factorId: factor?.factorId, mfaEnrollmentId: factor?.uid});
            await multiFactor(user).unenroll(factor);
            setEnrolledFactor(null);
            enqueueSnackbar('Two-factor authentication disabled', {variant: 'success'});
            mfaEvent('MFA unenrolled', {factorId: factor?.factorId});
        } catch (e) {
            mfaBreadcrumb('unenroll: threw', {code: e.code});
            if (e.code === 'auth/requires-recent-login') {
                enqueueSnackbar('Please log out and log back in, then try again.', {variant: 'warning'});
            } else {
                setError(e.message);
            }
        }

        setLoading(false);
    }, [user, confirm, enqueueSnackbar]);

    // Enrolled — show status inline with disable option (not in dialogOnly mode)
    if (!dialogOnly && enrolledFactor) {
        const method = FACTOR_LABELS[enrolledFactor.factorId] || enrolledFactor.factorId;
        return (
            <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                action={
                    <Button color="inherit" size="small" onClick={handleUnenroll} disabled={loading}>
                        Disable
                    </Button>
                }
            >
                Two-factor authentication is enabled via <strong>{method}</strong>
            </Alert>
        );
    }

    // dialogOnly mode — only render the dialog, no button
    if (dialogOnly && !open) return null;

    const qrUrl = totpSecret
        ? totpSecret.generateQrCodeUrl(user.email, siteName)
        : null;

    return (
        <>
            {!dialogOnly && (
                <Button
                    variant="outlined"
                    startIcon={<SecurityIcon />}
                    onClick={handleOpen}
                    data-testid="mfa-setup-button"
                >
                    Set Up Two-Factor Authentication
                </Button>
            )}

            <ResponsiveDialog open={open} onClose={handleClose} maxWidth="sm" data-testid="mfa-dialog">
                <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
                <DialogContent dividers>
                    {step === 'choose' && (
                        <Stack spacing={1}>
                            <Typography variant="body2" sx={{pb: 1}}>
                                Choose how you want to verify your identity when signing in.
                            </Typography>

                            <Button
                                variant="outlined"
                                startIcon={<SecurityIcon />}
                                onClick={startTotpEnrollment}
                                disabled={loading}
                                loading={loading}
                                sx={{justifyContent: 'flex-start'}}
                                data-testid="mfa-totp-option"
                            >
                                Authenticator App
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<PhoneIcon />}
                                onClick={() => setStep('sms')}
                                disabled={loading}
                                sx={{justifyContent: 'flex-start'}}
                                data-testid="mfa-sms-option"
                            >
                                SMS Text Message
                            </Button>
                        </Stack>
                    )}

                    {step === 'totp' && (
                        <Stack spacing={2} sx={{pt: 1}}>
                            {!totpSecret ? (
                                <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    <Typography variant="body2">
                                        Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                                    </Typography>

                                    <Box sx={{display: 'flex', justifyContent: 'center', p: 2, bgcolor: 'white', borderRadius: 1, width: 'fit-content', mx: 'auto'}}>
                                        <QRCodeSVG value={qrUrl} size={200} />
                                    </Box>

                                    <Typography variant="caption" sx={{wordBreak: 'break-all', textAlign: 'center'}}>
                                        Manual entry key: {totpSecret.secretKey}
                                    </Typography>

                                    <TextField
                                        label="Verification Code"
                                        value={verificationCode}
                                        onChange={e => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                            setVerificationCode(val);
                                            if (val.length === 6) {
                                                completeTotpEnrollment(val);
                                            }
                                        }}
                                        placeholder="Enter 6-digit code"
                                        inputProps={{maxLength: 6, inputMode: 'numeric', autoComplete: 'one-time-code'}}
                                        disabled={loading}
                                        autoFocus
                                    />
                                </>
                            )}
                        </Stack>
                    )}

                    {step === 'sms' && (
                        <Stack spacing={2} sx={{pt: 1}}>
                            <Typography variant="body2">
                                {phoneNumber
                                    ? 'We\'ll send a verification code to your phone number on file. You can change it if needed.'
                                    : 'Enter your phone number to receive verification codes via SMS.'}
                            </Typography>

                            <MuiTelInput
                                label="Phone Number"
                                value={phoneNumber}
                                onChange={value => setPhoneNumber(value)}
                                defaultCountry="CA"
                                onlyCountries={['CA', 'US']}
                                disabled={loading}
                                autoFocus={!phoneNumber}
                                inputProps={{'data-testid': 'mfa-phone-input'}}
                            />
                        </Stack>
                    )}

                    {step === 'sms-verify' && (
                        <Stack spacing={2} sx={{pt: 1}}>
                            <Typography variant="body2">
                                Enter the verification code sent to your phone
                            </Typography>

                            <TextField
                                label="Verification Code"
                                value={smsCode}
                                onChange={e => {
                                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                    setSmsCode(val);
                                    if (val.length === 6) {
                                        completeSmsEnrollment(val);
                                    }
                                }}
                                placeholder="Enter 6-digit code"
                                inputProps={{maxLength: 6, inputMode: 'numeric', autoComplete: 'one-time-code'}}
                                disabled={loading}
                                autoFocus
                            />

                            {smsSent && <Alert severity="info">Verification code sent</Alert>}

                            {resendCountdown > 0 ? (
                                <Typography variant="caption" sx={{color: 'text.secondary'}}>
                                    Resend code in {resendCountdown}s
                                </Typography>
                            ) : (
                                <Button size="small" onClick={sendSmsCode} disabled={loading}>
                                    Resend code
                                </Button>
                            )}
                        </Stack>
                    )}

                    {step === 'reauth' && (
                        <Stack spacing={2} sx={{pt: 1}}>
                            <Alert severity="info">
                                For security, please enter your password to continue.
                            </Alert>

                            <TextField
                                label="Password"
                                type="password"
                                value={reauthPassword}
                                onChange={e => setReauthPassword(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && reauthPassword) {
                                        handleReauthenticate();
                                    }
                                }}
                                disabled={loading}
                                autoFocus
                                data-testid="reauth-password"
                            />
                        </Stack>
                    )}

                    {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}

                    <div ref={recaptchaRef} />
                </DialogContent>
                <DialogActions>
                    {step !== 'choose' && (
                        <Button onClick={() => {
                            if (step === 'sms-verify') {
                                setStep('sms');
                                setSmsCode('');
                                setSmsSent(false);
                                setResendCountdown(0);
                                setError(null);
                            } else {
                                setStep('choose');
                                setSmsCode('');
                                setSmsSent(false);
                                setVerificationCode('');
                                setError(null);
                            }
                        }} disabled={loading}>
                            Back
                        </Button>
                    )}
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    {step === 'totp' && totpSecret && (
                        <Button
                            variant="contained"
                            onClick={completeTotpEnrollment}
                            disabled={loading || verificationCode.length !== 6}
                            loading={loading}
                        >
                            Verify & Enable
                        </Button>
                    )}
                    {step === 'sms' && (
                        <Button
                            variant="contained"
                            onClick={sendSmsCode}
                            disabled={loading || !phoneNumber}
                            loading={loading}
                        >
                            Send Code
                        </Button>
                    )}
                    {step === 'sms-verify' && (
                        <Button
                            variant="contained"
                            onClick={completeSmsEnrollment}
                            disabled={loading || smsCode.length !== 6}
                            loading={loading}
                        >
                            Verify & Enable
                        </Button>
                    )}
                    {step === 'reauth' && (
                        <Button
                            variant="contained"
                            onClick={handleReauthenticate}
                            disabled={loading || !reauthPassword}
                            loading={loading}
                        >
                            Continue
                        </Button>
                    )}
                </DialogActions>
            </ResponsiveDialog>
        </>
    );
}
